import React, { useState } from 'react';
import shortid from 'shortid';
import PropTypes from 'prop-types';
import { TextField as MUITextField, InputAdornment, SvgIcon } from '@mui/material';
import IconButton from '../IconButton';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import { getClearIconOption } from './services';
import './styles/textField.scss';

/** @component
 * @param {Object} props
 * @param {String} props.fieldId - generate id with shortid, or other suitable method and pass in as prop if needed
 */

export default function TextField({
  localized = {},
  id,
  type = 'text',
  variant = 'outlined',
  className = '',
  helperText = '',
  uppercase,
  isInErrorState = false,
  showErrorMessage = true,
  error,
  endIcon = '',
  handleClearField,
  clearIconVariant = '',
  isClearIconVisible = true,
  readOnly = false,
  textFieldColor,
  InputProps,
  fieldId,
  disabled,
  inputPlaceholder,
  startIcon: StartIcon,
  ...otherProps
}) {
  const [localFieldId] = useState(fieldId || shortid.generate() || '');
  const [showPassword, setShowPassword] = useState(false);
  let inputClassName = 'text-field ' + className ?? '' + `${disabled && 'text-field__disabled'}`;

  if (type === 'password' || error) {
    inputClassName += ' text-field--with-adornment';
  }

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleIconClick = () => {
    setShowPassword(!showPassword);
  };

  function getAdornmentIcon() {
    if (type === 'password') {
      return (
        <InputAdornment position='end'>
          <IconButton
            title={localized['textfield.passwordVisibility']}
            onClick={() => handleIconClick()}
            onMouseDown={handleMouseDownPassword}
          >
            {showPassword ? (
              <VisibilityOutlinedIcon
                className='text-field__visibility-icon'
                aria-label={localized['textfield.showPassword']}
              />
            ) : (
              <VisibilityOffOutlinedIcon
                className='text-field__visibility-icon'
                aria-label={localized['textfield.hidePassword']}
              />
            )}
          </IconButton>
        </InputAdornment>
      );
    } else if (type === 'text' && handleClearField && isClearIconVisible) {
      const { icon: ClearIcon } = getClearIconOption(clearIconVariant);
      return (
        <InputAdornment position='end'>
          <IconButton
            disabled={disabled}
            onClick={() => handleClearField()}
          >
            <SvgIcon
              disabled={disabled}
              fontSize='medium'
              className='text-field__clear-icon'
              aria-label={localized['textfield.clearInput']}
              component={ClearIcon}
            />
          </IconButton>
        </InputAdornment>
      );
    } else if (endIcon) {
      return (
        <InputAdornment position='end'>
          <IconButton disabled>
            <img
              src={endIcon}
              alt='icon field'
            />
          </IconButton>
        </InputAdornment>
      );
    }
  }

  return (
    <MUITextField
      className={inputClassName}
      disabled={disabled}
      variant={variant}
      InputLabelProps={{
        ...(uppercase ? { style: { textTransform: 'uppercase' } } : {})
      }}
      id={id || fieldId || localFieldId}
      error={isInErrorState || !!error}
      helperText={showErrorMessage && error ? error : helperText}
      {...(textFieldColor && { color: textFieldColor })}
      type={(type === 'password' && !showPassword ? 'password' : 'text') || type}
      InputProps={{
        placeholder: inputPlaceholder ?? '',
        readOnly,
        ...((type === 'password' || error || handleClearField || endIcon) && {
          endAdornment: error ? (
            <InputAdornment position='end'>
              <IconButton
                title={
                  error
                    ? localized['textfield.inputError']
                    : type === 'password'
                      ? localized['textfield.passwordVisibility']
                      : ''
                }
                disabled={!!error}
                {...(!!error && { 'aria-hidden': true })}
              >
                <ErrorIcon
                  className='text-field__error-icon'
                  aria-label={localized['textfield.inputError']}
                />
              </IconButton>
            </InputAdornment>
          ) : (
            getAdornmentIcon()
          ),
          ...(StartIcon
            ? {
                startAdornment: (
                  <InputAdornment
                    position='start'
                    className={isInErrorState || !!error ? 'start-icon--error' : ''}
                  >
                    <StartIcon className='text-field__start-icon' />
                  </InputAdornment>
                )
              }
            : {})
        }),
        ...(InputProps || {})
      }}
      {...(otherProps || {})}
    />
  );
}

TextField.propTypes = {
  localized: PropTypes.object,
  className: PropTypes.string,
  type: PropTypes.string,
  variant: PropTypes.string,
  helperText: PropTypes.string,
  uppercase: PropTypes.bool,
  isInErrorState: PropTypes.bool,
  readOnly: PropTypes.bool,
  id: PropTypes.string,
  fieldId: PropTypes.string,
  textFieldColor: PropTypes.string,
  endIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  error: PropTypes.string,
  InputProps: PropTypes.object,
  showErrorMessage: PropTypes.bool,
  handleClearField: PropTypes.func,
  clearIconVariant: PropTypes.oneOf(['clear', 'filled', 'outlined']),
  isClearIconVisible: PropTypes.bool,
  disabled: PropTypes.bool,
  startIcon: PropTypes.any,
  inputPlaceholder: PropTypes.string
};
